"use client";

import { FC, ReactNode, useEffect } from "react";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import { usePathname } from "next/navigation";
import { CaretDownLine } from "@/icons";
import cn from "classnames";
import { getDataLinkAttributes } from "@/helpers";
import Link from "@/components/Link";

export type LeftRailNavigationNestedProps = {
  navItemsHeadline?: string;
  navItems: {
    name: string;
    href: string;
    dlt: string;
    dld: string;
    subItems?: {
      name: string;
      href: string;
      dlt: string;
      dld: string;
    }[];
  }[];
  children?: ReactNode[];
};

const LeftRailNavigationNested: FC<LeftRailNavigationNestedProps> = ({
  navItems,
  navItemsHeadline,
  children,
}) => {
  const pathname = usePathname();

  // Calculate activeItemIndex during render instead of in effect
  const matchingIndex = navItems.findIndex((item) =>
    item.subItems?.some((subItem) => subItem.href.split("#")[0] === pathname),
  );
  const activeItemIndex = matchingIndex !== -1 ? matchingIndex : null;

  useEffect(() => {
    const hash = window.location.hash.slice(1);

    if (hash) {
      // Double rAF: wait two frames for the Disclosure panel to render before scrolling.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          document.getElementById(hash)?.scrollIntoView();
          document.getElementById(hash)?.focus();
        });
      });
    }
  }, []);

  const LeftRailNavigationStructure = (
    <div className="relative top-0 mr-6 ml-9 flex h-max w-full max-w-71.75 flex-col gap-4 sm:max-w-88.75 lg:sticky lg:ml-0 lg:shrink-0">
      {navItemsHeadline && (
        <p className="text-subhead-s leading-xs text-headline mb-4 font-bold">
          {navItemsHeadline}
        </p>
      )}
      <ul className="after:border-symbol-cool-gray-600-graphic relative top-0 flex flex-col gap-4 after:absolute after:top-3 after:bottom-2.25 after:-left-10.5 after:w-[21.8px] after:border-[3px] after:border-r-0 after:border-dotted">
        {navItems.map((item, index) => (
          <Disclosure
            key={`${item.name}-${index}-${activeItemIndex}`}
            as="li"
            defaultOpen={activeItemIndex === index}
          >
            {({ open }) => (
              <>
                <DisclosureButton
                  className={cn(
                    "text-subhead-2xs data-focus:outline-swdc-blue-400 active:text-tab-link-pressed hover:text-tab-link-hover text-headline relative flex w-full flex-row items-center justify-between rounded-xs font-bold hover:underline active:underline data-focus:outline-2 data-focus:outline-offset-2",
                    activeItemIndex === index &&
                      "before:bg-symbol-orange-400-graphic after:bg-symbol-orange-400-graphic before:absolute before:top-[calc(50%-4px)] before:-left-6.5 before:z-20 before:size-3 before:rounded-full after:absolute after:top-1/2 after:-left-23.75 after:z-20 after:h-0.75 after:w-20 after:animate-[vertical-tab-growing_0.4s_ease-in_1] after:rounded-lg after:transition-all",
                  )}
                  {...getDataLinkAttributes({
                    dlt: item.dlt,
                    dld: item.dld,
                  })}
                >
                  <p className="text-left">{item.name}</p>
                  <CaretDownLine
                    className={cn(
                      "flex shrink-0 basis-4.75",
                      open
                        ? "ease rotate-180 transition duration-200"
                        : "ease rotate-0 transition duration-200",
                    )}
                    fill="var(--color-symbol-orange-400-graphic)"
                  />
                </DisclosureButton>
                <DisclosurePanel
                  as="ul"
                  className="text-body-s flex flex-col gap-4 pt-4 pl-8"
                >
                  {item.subItems?.map((subItem, ix) => (
                    <li key={`${subItem.name}-${ix}`}>
                      <Link
                        href={subItem.href}
                        className="text-tab-link active:text-tab-link-pressed hover:text-tab-link-hover font-normal hover:underline active:underline"
                        {...getDataLinkAttributes({
                          dlt: subItem.dlt,
                          dld: subItem.dld,
                        })}
                      >
                        {subItem.name}
                      </Link>
                    </li>
                  ))}
                </DisclosurePanel>
              </>
            )}
          </Disclosure>
        ))}
      </ul>
    </div>
  );

  return children !== undefined ? (
    <div className="flex flex-col lg:flex-row">
      {LeftRailNavigationStructure}
      {children}
    </div>
  ) : (
    <>{LeftRailNavigationStructure}</>
  );
};

export default LeftRailNavigationNested;
